/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Spin,
  Modal,
  message,
  DatePicker,
  Card,
  TreeSelect,
  Tabs,
} from 'antd';
import { onError } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import createClave from 'utils/keyGenerator';
import { isMutable, isAuthorized } from 'utils/estadosGlobales';
import { ActionsNames, hasActionPermission } from 'utils/permission/index';
import { DATE_FRONTEND_FORMAT } from 'utils/formatValues';
import { makeGetOnFinish, makeOnChangeTabKey } from 'utils/tabsManagement';
import makeMessager from 'utils/complexFormsMessages';
import NumericInput from './NumericInput';
import LadaNumero from './LadaNumero';
import Direccion from './Direccion';

export const defaultFields = {
  clave: {
    name: 'clave',
    label: 'Clave',
    required: true,
    colWidth: 150,
    isKey: true,
  },
  nombre: {
    name: 'nombre',
    label: 'Nombre',
    required: true,
    colWidth: 200,
  },
  concepto: {
    name: 'concepto',
    label: 'Concepto',
    required: true,
    colWidth: 200,
  },
  descripcion: {
    name: 'descripcion',
    label: 'Descripción',
    required: true,
    colWidth: 200,
    breakpoints: { md: 16 },
  },
  observaciones: {
    name: 'observaciones',
    label: 'Observaciones',
    required: true,
    breakpoints: { md: 16 },
  },
  telefono: {
    name: 'telefono',
    lada: 'lada',
    label: 'Teléfono',
    type: 'phone',
  },
};

const defaultAddressConfig = { name: 'direccion', title: 'Dirección' };

const PlainCRUD = ({
  title,
  get,
  post,
  patch,
  delete: _delete,
  fields,
  withoutState,
  permission,
  readOnly,
  catalogs: fksCatalogs,
  tableProps,
  valuesToFormFormatter,
  valuesFormatter,
  initialValues,
  customModalWidth,
  extraFields,
  loading: _loading,
  form: _form,
  onCancelCallback,
  handleOnRowClick: _handleOnRowClick,
  paginated,
  disabledStateField,
  address,
  addressPermission,
  baseURI,
}) => {
  const inputRef = useRef();
  const [form] = _form ? [_form] : Form.useForm();
  const estadosGlobales = useSelector((s) => s.catalogs.estadosGlobales);
  const entidad = useSelector((s) => s.auth.entidad);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(_loading);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState(paginated ? { results: [] } : []);
  // const [tableData, setTableData] = useState([]);
  const [mutable, setMutable] = useState(true);
  const [hasAllViewPermission, setHasAllViewPermission] = useState(true);
  const [catalogs, setCatalogs] = useState({});
  const [plainCatalogs, setPlainCatalogs] = useState({});
  const [currentTabKey, setCurrentTabKey] = useState('general');
  const [formAddress, setFormAddress] = useState(null);

  useEffect(() => {
    setLoading(_loading);
  }, [_loading]);

  const columns = [
    ...fields.filter((e) => e?.colWidth)
      .map((f) => ({
        titleText: f.label,
        key: f.name,
        dataIndex: f.name,
        width: f.colWidth,
        align: f.align || f.colAlign,
        render: f.render,
        ellipsis: f.colWidth >= 350,
        catalog: f?.useCatalog
          ? { cat: plainCatalogs?.[f.name], labelProp: f.labelProp || 'descripcion' } : undefined,
      })),
    !withoutState && {
      titleText: 'Estado',
      key: 'estados_globales',
      dataIndex: 'estados_globales',
      width: 150,
      render: (val) => estadosGlobales.find((e) => e.id === val)?.descripcion,
      catalog: {
        cat: estadosGlobales,
        labelProp: 'descripcion',
        use: false,
      },
    },
  ].filter((e) => e);

  const fetchData = async (page = 1, params) => {
    setLoading(true);
    const _data = await get(paginated ? { ...params, page } : undefined);
    if (paginated) {
      _data.page = page;
    }
    setData(_data);
    // setTableData(_data);
    // if (fields.some((e) => e.useCatalog)) {
    //   // Insertando objetos de catalogos en propiedades fks de "data"
    //   setTableData(_data.map((item) => ({
    //     ...item,
    //     ...Object.fromEntries(
    //       fields.filter((f) => f.useCatalog).map(({ name: catalogName }) => [
    //         catalogName,
    //         _plainCatalogs[catalogName].find((e) => e.id === item[catalogName]),
    //       ]),
    //     ),
    //   })));
    // }
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;

    const fetchAll = async () => {
      setLoading(true);
      const results = await (fksCatalogs.length
        ? Promise.all(fksCatalogs.map((e) => e.get())) : undefined);
      const _catalogs = fksCatalogs.length
        ? Object.fromEntries(results.map((e, idx) => [fksCatalogs[idx].formItemName, e])) : null;
      if (fksCatalogs.length) setCatalogs(_catalogs);
      if (fields.some((e) => e.useCatalog)) {
        const _plainCatalogs = Object.fromEntries(
          fksCatalogs
            .map(({ formItemName: catalogName, extractor }) => [
              catalogName,
              extractor?.(_catalogs[catalogName]) || _catalogs[catalogName],
            ]),
        );
        setPlainCatalogs(_plainCatalogs);
      }
      if (get) {
        await fetchData(paginated ? 1 : undefined);
      }
      if (mounted) {
        setLoading(false);
      }
    };
    const validatePermission = () => {
      const _hasAllViewPermission = fksCatalogs
        .map((c) => hasActionPermission(c.permission, ActionsNames.view))
        .every((e) => e);
      if (!_hasAllViewPermission) {
        message.warn('Hacen falta permisos en los catálogos relacionados a este módulo');
      }
      if (mounted) {
        setHasAllViewPermission(_hasAllViewPermission);
        fetchAll();
      }
    };
    validatePermission();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setSelectedRowKeys([]);
    setMutable(true);
    onCancelCallback();
    setCurrentTabKey('general');
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = valuesFormatter(form.getFieldsValue());
      values.entidad = entidad.id;
      const [key] = selectedRowKeys;
      const success = await (key ? patch(key, values) : post(values));
      if (success) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };
  const complexForms = [{ key: 'direccion', formInstance: formAddress, permission: addressPermission }];

  const basicKeys = ['general'];

  const getOnFinish = makeGetOnFinish(basicKeys, complexForms, currentTabKey, onFinish);

  const deleteItem = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const deleted = await _delete(key);
    if (deleted) {
      setVisibleAlert(false);
      onCancel();
      await fetchData();
    }
    setLoading(false);
  };

  const handleOnRowClick = (_record) => {
    const keyProp = tableProps?.expandable ? 'key' : 'id';
    const key = _record[keyProp];
    setSelectedRowKeys([key]);
    const record = (data.results || data).find((e) => e[keyProp] === key) || _record;
    setMutable(isMutable(record));
    form.setFieldsValue(valuesToFormFormatter(record));
    _handleOnRowClick(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    const keyField = fields.find((f) => f.isKey);
    if (keyField?.autoGenerate) {
      createClave(data, form, { keyProp: keyField.name });
    }
    setVisible(true);
  };

  const onClickEdit = () => setVisible(true);

  const onClickDelete = () => {
    const [key] = selectedRowKeys;
    const match = (data.results || data).find((e) => e.id === key);
    if (isMutable(match)) {
      setVisibleAlert(true);
    } else {
      message.warn(`No se puede eliminar un ${title} ${isAuthorized(match) ? 'autorizado' : 'cancelado'}`);
    }
  };

  const onClickExpand = () => setVisible(true);

  const rules = {
    required: [{ required: true }],
  };

  const controls = () => {
    if (tableProps?.customActions) return undefined;
    if (readOnly) {
      return {
        onClickExpand,
      };
    }
    return {
      onClickAdd,
      onClickEdit,
      onClickDelete,
    };
  };

  const getTitle = () => {
    if (readOnly) {
      return 'Vista previa';
    }
    return `${selectedRowKeys.length ? 'Editar' : 'Agregar'} ${title}`;
  };

  const getRules = (field) => {
    if (readOnly || !mutable) {
      return null;
    }
    return field.rules || (field.required && rules.required) || null;
  };

  useEffect(() => {
    if (visible) {
      // setTimeout(inputRef.current.focus);
    }
  }, [visible]);

  const generateKey = (val, catalog, filterProp) => {
    const keyField = fields.find((f) => f.isKey);
    if (val) {
      const parent = catalog.find((e) => e.value === val);
      createClave(data, form, { keyProp: keyField.name, filter: { parent, prop: filterProp } });
    } else {
      form.resetFields([keyField.name]);
    }
  };

  const disabled = !mutable || readOnly;
  const addressConfig = address === true
    ? defaultAddressConfig : { ...defaultAddressConfig, ...address };
  const useModal = fields.length < 6 && !addressConfig;

  const isStateFieldDisabled = () => {
    if (disabledStateField) return true;
    return !selectedRowKeys.length || !mutable || readOnly;
  };

  const getControl = (field, useInputRef) => {
    if (field.type === 'textarea') {
      return (
        <Input.TextArea
          allowClear
          ref={useInputRef ? inputRef.current : null}
          autoSize={field.autoSize || { minRows: 3 }}
          disabled={disabled || field.disabled || (field.disabledOnEdit && selectedRowKeys.length)}
        />
      );
    }
    if (field.type === 'treeselect') {
      return (
        <TreeSelect
          onChange={field.manageKey
            ? (val) => generateKey(val, plainCatalogs[field.name], field.name) : null}
          showSearch
          treeNodeFilterProp="title"
          style={{ width: '100%' }}
          dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
          treeData={catalogs[field.name]}
          allowClear
          disabled={disabled || field.disabled || (field.disabledOnEdit && selectedRowKeys.length)}
        />
      );
    }
    if (field.type === 'select') {
      return (
        <Select
          onChange={field.manageKey
            ? (val) => generateKey(val, plainCatalogs[field.name], field.name) : null}
          render={field.render}
          labelProp={field.labelProp}
          dataSource={typeof field?.filter === 'function'
            ? (catalogs[field.name] || []).filter(field.filter)
            : catalogs[field.name] || field.dataSource || []}
          trueFalse={field.trueFalse || (!field.useCatalog && !field.dataSource)}
          disabled={disabled || field.disabled || (field.disabledOnEdit && selectedRowKeys.length)}
          mode={field?.mode || null}
        />
      );
    }
    if (field.type?.includes('date')) {
      return (
        <DatePicker
          picker={field.type.replace('date.', '')}
          format={field.type === 'date' ? DATE_FRONTEND_FORMAT : null}
          placeholder=""
          disabled={disabled || field.disabled || (field.disabledOnEdit && selectedRowKeys.length)}
        />
      );
    }
    return (
      <Input
        allowClear
        ref={useInputRef ? inputRef.current : null}
        disabled={disabled || field.disabled || (field.disabledOnEdit && selectedRowKeys.length)}
      />
    );
  };

  const getItem = (field, useInputRef, order) => {
    if (field.type === 'phone') {
      return (
        <LadaNumero
          names={{ lada: field.lada, numero_de_telefono: field.name }}
          required={field.required}
          fullSize={useModal}
          order={order}
          rules={field.rules}
        />
      );
    }
    if (['decimal', 'number'].includes(field.type)) {
      return (
        <Col {...(useModal ? { span: 24 } : { xs: 24, sm: 24, md: 8 })} order={order}>
          <NumericInput
            name={field.name}
            label={field.label}
            disabled={disabled || field.disabled
              || (field.disabledOnEdit && selectedRowKeys.length)}
            // ref={_inputRef}
            required={field.required}
            decimal={field.type === 'decimal'}
            precision={field.precision}
            commas={field.commas}
            min={field.min}
            max={field.max}
            allowZero={field?.allowZero ?? true}
          />
        </Col>
      );
    }

    return (
      <Col
        {...(useModal ? { span: 24 } : { xs: 24, sm: 24, md: field.breakpoints?.md || 8 })}
        order={order}
      >
        <Form.Item
          name={field.name}
          label={field.label}
          rules={getRules(field, useInputRef)}
          normalize={field.normalize}
        >
          {getControl(field, useInputRef)}
        </Form.Item>
      </Col>
    );
  };

  const MyForm = () => (
    <Form
      layout="vertical"
      form={form}
      onFinish={() => onFinish()}
      scrollToFirstError
      className={disabled ? 'without-feedback' : ''}
      initialValues={{
        estados_globales: 1,
        ...initialValues,
      }}
      style={{ display: visible ? 'initial' : 'none' }}
    >
      <Row gutter={10}>
        {fields.filter((f) => !f.tableOnly).map((field, idx) => (
          <React.Fragment key={field.name}>
            {getItem(field, idx === 0 ? inputRef.current : null, idx + 1)}
          </React.Fragment>
        ))}
        {extraFields.map((field, idx) => (
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={idx}>
            {field}
          </React.Fragment>
        ))}
        {!withoutState && (
          <Col
            {...(useModal ? { span: 24 } : { xs: 24, sm: 24, md: 8 })}
            order={fields.length + extraFields.length + 1}
          >
            <Form.Item
              name="estados_globales"
              label="Estado"
              rules={readOnly || !mutable ? null : rules.required}
              hasFeedback
            >
              <Select
                disabled={isStateFieldDisabled()}
                dataSource={estadosGlobales}
              />
            </Form.Item>
          </Col>
        )}
      </Row>
      <Form.Item name="id" hidden>
        <Input />
      </Form.Item>
      <Form.Item hidden>
        <Button htmlType="submit" />
      </Form.Item>
    </Form>
  );

  const getPaginationProps = () => {
    if (paginated) {
      return {
        paginationCallback: fetchData,
        count: data.count,
        pagination: false,
        currentPage: data.page,
      };
    }
    return null;
  };

  const showMsg = makeMessager(setCurrentTabKey, fetchData);

  const onChangeTabKey = makeOnChangeTabKey(
    basicKeys,
    complexForms,
    currentTabKey,
    setCurrentTabKey,
    showMsg,
  );

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {(useModal || (!useModal && !visible)) && (
          <Table
            cols={columns}
            data={data.results || data}
            rowSelection={rowSelection}
            handleOnRowClick={handleOnRowClick}
            controls={controls()}
            mobileColIndex={0}
            allowSort={false}
            permission={permission}
            hasAllViewPermission={hasAllViewPermission}
            rowKey={tableProps.expandable ? 'key' : 'id'}
            allowExpand={readOnly}
            {...getPaginationProps()}
            {...tableProps}
          />
        )}
        {!useModal && visible && (
          <Card
            className="form-container"
            title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} ${title}`}
            extra={(
              <FormSubmitControls
                onFinish={getOnFinish()}
                onCancel={onCancel}
                loading={loading}
                permission={permission}
              />
            )}
            bordered={false}
          >
            {address ? (
              <Tabs
                onChange={onChangeTabKey}
                activeKey={currentTabKey}
                type="card"
              >
                <Tabs.TabPane tab="General" key="general">
                  <MyForm />
                </Tabs.TabPane>
                <Tabs.TabPane
                  tab={addressConfig.title}
                  key="direccion"
                  forceRender={!!selectedRowKeys?.length}
                  disabled={!selectedRowKeys.length}
                >
                  <Direccion
                    setFormAddress={setFormAddress}
                    currentURI={baseURI}
                    parentID={selectedRowKeys[0]}
                    setLoading={setLoading}
                    setCurrentTabKey={setCurrentTabKey}
                    addressPropName={addressConfig.name}
                    callback={(values, _continue) => {
                      if (!_continue) {
                        onCancel();
                      } else {
                        const [key] = selectedRowKeys;
                        const clone = [...data.results];
                        const results = clone.map((e) => (e.id === key
                          ? ({ ...e, [addressConfig.name]: values?.id }) : e));
                        setData({ ...data, results });
                      }
                    }}
                    permission={addressPermission}
                    mutable={mutable}
                  />
                </Tabs.TabPane>
              </Tabs>
            ) : (<MyForm />)}
          </Card>
        )}
        {useModal && (
          <Modal
            open={visible}
            title={(
              <FormSubmitControls
                label={getTitle()}
                onFinish={readOnly || !mutable ? null : form.submit}
                onCancel={onCancel}
                loading={loading}
                mutable={mutable}
                permission={permission}
              />
            )}
            onCancel={onCancel}
            footer={null}
            closable={false}
            maskClosable={!loading}
            keyboard={!loading}
            forceRender
            width={customModalWidth}
          >
            <MyForm />
          </Modal>
        )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => {
            setVisibleAlert(false);
            setSelectedRowKeys([]);
          }}
          visible={visibleAlert}
          content={`${title} ${form.getFieldValue(fields[0]?.name || 'descripcion')}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

PlainCRUD.propTypes = {
  title: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
  withoutState: PropTypes.bool,
  get: PropTypes.func,
  post: PropTypes.func,
  patch: PropTypes.func,
  delete: PropTypes.func,
  fields: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    normalize: PropTypes.func,
    type: PropTypes.oneOf(['select', 'treeselect', 'decimal', 'number', 'textarea', 'phone', 'date', 'date.year']),
    colWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    colAlign: PropTypes.oneOf(['left', 'right']),
    autoSize: PropTypes.shape({
      minRows: PropTypes.number,
      maxRows: PropTypes.number,
    }),
    breakpoints: PropTypes.shape({
      md: PropTypes.number,
    }),
    isKey: PropTypes.bool,
    autoGenerate: PropTypes.bool,
    manageKey: PropTypes.bool,
    required: PropTypes.bool,
    rules: PropTypes.arrayOf(PropTypes.shape({})),
    filter: PropTypes.func,
    trueFalse: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    tableOnly: PropTypes.bool,
  })),
  permission: PropTypes.shape({
    add: PropTypes.bool.isRequired,
    authorize: PropTypes.bool.isRequired,
    cancel: PropTypes.bool.isRequired,
    change: PropTypes.bool.isRequired,
    delete: PropTypes.bool.isRequired,
    export: PropTypes.bool.isRequired,
    import: PropTypes.bool.isRequired,
    sync: PropTypes.bool.isRequired,
    view: PropTypes.bool.isRequired,
  }),
  catalogs: PropTypes.arrayOf(PropTypes.shape({
    permission: PropTypes.string,
    get: PropTypes.func.isRequired,
    formItemName: PropTypes.string.isRequired,
    extractor: PropTypes.func,
  })),
  tableProps: PropTypes.shape(),
  valuesToFormFormatter: PropTypes.func,
  valuesFormatter: PropTypes.func,
  initialValues: PropTypes.shape(),
  customModalWidth: PropTypes.oneOf(['string', 'number']),
  extraFields: PropTypes.node,
  loading: PropTypes.bool,
  form: PropTypes.shape(),
  onCancelCallback: PropTypes.func,
  handleOnRowClick: PropTypes.func,
  paginated: PropTypes.bool,
  disabledStateField: PropTypes.bool,
  address: PropTypes.oneOfType([PropTypes.bool, PropTypes.shape()]),
  addressPermission: PropTypes.shape(),
  baseURI: PropTypes.string,
};

PlainCRUD.defaultProps = {
  readOnly: false,
  withoutState: false,
  fields: [defaultFields.descripcion],
  get: null,
  post: null,
  patch: null,
  delete: null,
  permission: null,
  catalogs: [],
  tableProps: {},
  valuesToFormFormatter: (values) => values,
  valuesFormatter: (values) => values,
  initialValues: {},
  customModalWidth: undefined,
  extraFields: [],
  loading: true,
  form: null,
  onCancelCallback: () => { },
  handleOnRowClick: () => { },
  paginated: false,
  disabledStateField: false,
  address: false,
  addressPermission: null,
  baseURI: null,
};

export default PlainCRUD;
